import {
  type ComponentPropsWithoutRef,
  type PropsWithChildren,
  type ReactNode,
  useRef
} from 'react';
import * as ContextMenu from '@radix-ui/react-context-menu';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { useLocation } from '@reach/router';
import { motion, MotionConfig, useTransform } from 'framer-motion';
import styled from 'styled-components';

import { Button } from '@/components/design-system';
import { LinkWithQuery } from '@/components/global/LinkWithQuery';
import { gridColorSelector } from '@/components/v2/GridLines';
import { LayoutSubgrid } from '@/components/v2/Page/LayoutGrid';
import { ReactComponent as HexLogo } from '@/images/hex-logo.svg';
import { LOGIN_LINK, SIGNUP_LINK } from '@/utils';

import { NavLAccent, type Position } from './Accent';
import { leftNav, rightNav } from './config';
import { useHeaderUI, useHeaderUIRefs } from './HeaderUIProvider';
import { MediaContextMenu } from './MediaContextMenu';
import { MobileTrigger } from './MobileNav';
import {
  NavDropdownProvider,
  useNavDropdownDispatchContext
} from './NavDropdownProvider';
import { NavMenuItem } from './NavMenuItem';
import { NavMenuViewport } from './NavMenuViewport';

const NavigationRootEl = styled.div`
  position: relative;
  display: grid;
  align-items: center;
  grid-template-columns: 1fr max-content 1fr;
  padding: 0.75rem 0;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    padding: 0.5rem 0;
  }
`;

const Hex = styled(HexLogo)`
  display: block;
  will-change: transform;

  [data-theme='pink'] & {
    color: ${({ theme }) => theme.paletteCssVars.fgLoud};
  }
`;

const NavigationList = styled(NavigationMenu.List)`
  gap: 0;
`;

const LeftNav = styled(NavigationList)`
  grid-column: 1 / span 1;
  display: flex;
  justify-content: flex-end;
  padding-right: 2.25rem;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    display: none;
    visibility: hidden;
  }
`;

const RightNav = styled(NavigationList)`
  grid-column: 3 / span 1;
  display: flex;
  justify-content: flex-start;
  padding-left: 1.75rem;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    display: none;
    visibility: hidden;
  }
`;

const LogInLink = styled(LinkWithQuery)`
  position: relative;
  padding: 0.6rem 0.75rem;
  border-bottom: 0;
  font-size: ${({ theme }) => theme.fontSize.body.base};
  line-height: 1;
  border-radius: calc(1rem / 16);
  font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
  color: ${({ theme }) => theme.paletteCssVars.fg1200};
  white-space: nowrap;
  transition: background-color 0.2s ease;

  &:hover,
  &:focus-visible {
    color: ${({ theme }) => theme.paletteCssVars.fg1200};
    background-color: ${({ theme }) => theme.paletteCssVars.fgA500};
  }
`;

const SignUpButton = styled(Button)`
  margin-left: 0.75rem;
  font-size: ${({ theme }) => theme.fontSize.body.base};
  line-height: 1;
`;

const NavWrapper = styled(LayoutSubgrid)`
  position: relative;
  isolation: isolate;

  &::after {
    grid-row: 1;
    content: '';
    height: 1px;
    background: ${({ theme }) => gridColorSelector(theme, 'major')};
    grid-column: 2 / -2;
  }
`;

const accentPositions: Position[] = ['tl', 'tr', 'br', 'bl'];

const NavBorderBottom = styled.div`
  align-self: flex-end;
  background-color: ${({ theme }) => gridColorSelector(theme, 'minor')};
  height: 1px;
  grid-column: 1;

  &[data-position='right'] {
    grid-column: -1;
  }
`;

const Background = styled(LayoutSubgrid)`
  position: relative;
  align-self: stretch;
  z-index: -1;

  &::after,
  &::before {
    content: '';
    border-top: 1px solid ${({ theme }) => gridColorSelector(theme, 'minor')};
    justify-self: stretch;
    grid-row: 1;
  }

  &::before {
    z-index: 1;
    grid-column: 1;
    border-right: 1px solid ${({ theme }) => gridColorSelector(theme, 'major')};
  }

  &::after {
    z-index: 1;
    grid-column: -1;
    border-left: 1px solid ${({ theme }) => gridColorSelector(theme, 'major')};
  }
`;

const BackgroundInner = styled.div`
  --stop-1: rgba(0 0 0 / 0.48);
  --stop-2: rgba(0 0 0 / 0);

  position: absolute;
  inset: 0 0 auto 0;
  height: 100%;
  grid-column: 1 / -1;

  mask-image: linear-gradient(to bottom, var(--stop-1) 30%, var(--stop-2));

  &::after,
  &::before {
    content: '';
    position: absolute;
    inset: 0;
  }

  &::before {
    background-color: ${({ theme }) => theme.paletteCssVars.fg100};
  }

  &::after {
    background-image: ${({ theme }) => theme.paperTextureCssVars.img};
    background-repeat: repeat;
    background-size: ${({ theme }) => theme.paperTextureCssVars.size};
    mix-blend-mode: ${({ theme }) => theme.paperTextureCssVars.mixBlendMode};
    background-position: 13px 27px;
    opacity: ${({ theme }) => theme.paperTextureCssVars.opacity};
  }
`;

const NavRootBorder = styled.div`
  position: absolute;
  inset: auto 0 0 0;
  height: 1px;
  background-color: ${({ theme }) => gridColorSelector(theme, 'major')};
`;

const NavShadow = styled.div`
  position: relative;
  grid-column: 1 / -1;
  grid-row: 1;
  height: 100%;
  width: 100%;
  pointer-events: none;
  z-index: 3;
  overflow-x: clip;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    box-shadow: 0px 2px 8px -2px hsl(0 0% 0% / 0.56);
  }

  [data-theme='pink'] &::after {
    box-shadow: 0px 3px 12px -3px rgba(100 63 63 / 0.32);
  }
`;

function Navigation({
  children,
  viewport
}: PropsWithChildren<{ viewport: ReactNode }>) {
  const { tickerHeight, smoothSquishProgress } = useHeaderUI();
  const { navRef } = useHeaderUIRefs();

  const tickerOffset = useTransform(
    smoothSquishProgress,
    [0, 1],
    [tickerHeight, 0]
  );

  const navOffset = useTransform(
    smoothSquishProgress,
    [0, 1],
    ['1.25rem', '0rem']
  );

  const viewportOffset = useTransform(smoothSquishProgress, () => {
    return `translateY(calc(${navOffset.get()} + ${tickerOffset.get()}px))`;
  });

  const BgStop1 = useTransform(
    smoothSquishProgress,
    [0, 1],
    ['rgba(0 0 0 / 0.48)', 'rgba(0 0 0 / 1)']
  );
  const BgStop2 = useTransform(
    smoothSquishProgress,
    [0, 1],
    ['rgba(0 0 0 / 0.0)', 'rgba(0 0 0 / 1)']
  );

  return (
    <>
      <NavWrapper as={motion.div} style={{ y: tickerOffset }}>
        <Background data-pile>
          <BackgroundInner
            as={motion.div}
            style={{ '--stop-1': BgStop1, '--stop-2': BgStop2 }}
          />
        </Background>
        <LayoutSubgrid as={motion.div} data-pile style={{ y: navOffset }}>
          <NavShadow
            as={motion.div}
            style={{ opacity: smoothSquishProgress }}
          />
          <LayoutSubgrid data-pile>
            <NavBorderBottom
              as={motion.div}
              style={{ opacity: smoothSquishProgress }}
            />
            <NavigationRootEl ref={navRef}>
              {children}
              <NavRootBorder
                as={motion.div}
                style={{
                  scaleX: smoothSquishProgress,
                  opacity: smoothSquishProgress
                }}
              />
            </NavigationRootEl>
            <NavBorderBottom
              as={motion.div}
              data-position="right"
              style={{ opacity: smoothSquishProgress }}
            />
          </LayoutSubgrid>
        </LayoutSubgrid>
        {accentPositions.map(position => (
          <NavLAccent key={position} position={position} />
        ))}
      </NavWrapper>
      <motion.div style={{ transform: viewportOffset, gridColumn: '1 / -1' }}>
        {viewport}
      </motion.div>
    </>
  );
}

const HomeNavLinkWrapper = styled.div`
  grid-column: 2 / span 1;
  width: 4.25rem;
  border: none;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    grid-column: 1 / span 1;
    margin-left: 0.9rem;
    width: 4.25rem;
  }
`;

function NavRoot({ children }: PropsWithChildren) {
  const handleValueChange = useNavDropdownDispatchContext();
  const ref = useRef<HTMLElement>(null);

  return (
    <NavigationMenu.Root
      asChild
      delayDuration={0}
      onValueChange={v => handleValueChange(v, ref)}>
      <LayoutSubgrid ref={ref} as="nav">
        {children}
      </LayoutSubgrid>
    </NavigationMenu.Root>
  );
}

export function NavContainer({ children }: PropsWithChildren) {
  return (
    <MotionConfig
      transition={{
        type: 'spring',
        duration: 0.32,
        bounce: 0
      }}>
      <NavDropdownProvider>
        <NavRoot>
          <Navigation viewport={<NavMenuViewport />}>{children}</Navigation>
        </NavRoot>
      </NavDropdownProvider>
    </MotionConfig>
  );
}

export function HomeNavLink(
  props: ComponentPropsWithoutRef<typeof HomeNavLinkWrapper>
) {
  const { pathname } = useLocation();
  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger asChild>
        <NavigationMenu.Link active={pathname === '/'} asChild>
          <HomeNavLinkWrapper as={LinkWithQuery} to="/" {...props}>
            <Hex />
          </HomeNavLinkWrapper>
        </NavigationMenu.Link>
      </ContextMenu.Trigger>
      <ContextMenu.Portal>
        <MediaContextMenu />
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}

export function Nav() {
  return (
    <NavContainer>
      <LeftNav>
        {leftNav.map(item => (
          <NavMenuItem key={item.title} item={item} />
        ))}
      </LeftNav>
      <HomeNavLink />
      <RightNav>
        {rightNav.map(item => (
          <NavMenuItem key={item.title} item={item} />
        ))}
        <NavigationMenu.Item>
          <NavigationMenu.Link asChild>
            <LogInLink isExternal to={LOGIN_LINK}>
              Log In
            </LogInLink>
          </NavigationMenu.Link>
        </NavigationMenu.Item>
        <NavigationMenu.Item>
          <SignUpButton
            alignText="center"
            link={`${SIGNUP_LINK}?source=nav`}
            size="small"
            stylez="loud"
            text="Get started"
          />
        </NavigationMenu.Item>
      </RightNav>
      <MobileTrigger />
    </NavContainer>
  );
}
